import { useState, useMemo, useEffect } from 'react';
import type { Device, Group } from '../types';
import { api } from '../utils/api';
import { numericLocaleCompare } from '../utils/sortUtils';
import moment from 'moment';

interface VitalSignsDeviceListProps {
  devices: Device[];
  className?: string;
}

interface DeviceHealthStats {
  deviceId: string;
  heartRate: number | null;
  bloodOxygen: number | null;
  stressLevel: 'Low' | 'Medium' | 'High' | 'Relaxed' | null;
}

export default function VitalSignsDeviceList({ devices: initialDevices, className }: VitalSignsDeviceListProps) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [activeDeviceId, setActiveDeviceId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [healthStats, setHealthStats] = useState<DeviceHealthStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Use initialDevices directly instead of storing in state
  const devices = initialDevices;

  // Debug: Log devices when they change
  useEffect(() => {
    console.log('VitalSignsDeviceList - Devices received:', devices.length, devices);
  }, [devices]);

  // Fetch companies and groups on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const groupsData = await api.getAllGroups();
        setGroups(groupsData);
      } catch (error) {
        console.error('Error fetching groups:', error);
      }
    };
    fetchData();
  }, []);

  // Sort devices by idEmployee first, then by name
  const sortedDevices = useMemo(() => {
    return [...devices].sort((a, b) => {
      if (a.idEmployee && b.idEmployee) {
        return numericLocaleCompare(a.idEmployee, b.idEmployee);
      }
      if (a.idEmployee && !b.idEmployee) return -1;
      if (!a.idEmployee && b.idEmployee) return 1;
      
      const nameA = (a.employee?.Nama || a.name || 'Unnamed').toLowerCase();
      const nameB = (b.employee?.Nama || b.name || 'Unnamed').toLowerCase();
      return numericLocaleCompare(nameA, nameB);
    });
  }, [devices]);

  // Auto-select first device on initial load
  useEffect(() => {
    if (sortedDevices.length > 0 && !activeDeviceId) {
      const firstDevice = sortedDevices[0];
      setActiveDeviceId(firstDevice.id);
      
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('device-select', {
            detail: { deviceId: firstDevice.id },
          }),
        );
      }, 100);
    }
  }, [sortedDevices, activeDeviceId]);

  // Fetch health data for all devices using single optimized endpoint
  const fetchHealthDataForAllDevices = async (date: string) => {
    try {
      setIsLoading(true);
      
      if (devices.length === 0) {
        setIsLoading(false);
        return;
      }

      console.log('Fetching health data for date:', date, 'devices:', devices.length);

      // Use single optimized endpoint
      const response = await api.getSummaryHealth({
        date: date,
        limit: 100
      });

      console.log('Summary health response:', response);

      if (response.success && response.data) {
        const stats: DeviceHealthStats[] = response.data.devices.map((device: any) => {
          let stressLevel: 'Low' | 'Medium' | 'High' | 'Relaxed' | null = null;
          const stressValue = device.vitalSigns.stressLevel;
          
          if (stressValue !== null && !isNaN(stressValue)) {
            if (stressValue >= 75) stressLevel = 'High';
            else if (stressValue >= 50) stressLevel = 'Medium';
            else if (stressValue >= 25) stressLevel = 'Low';
            else stressLevel = 'Relaxed';
          }

          const healthStat = {
            deviceId: device.deviceId,
            heartRate: device.vitalSigns.heartRate,
            bloodOxygen: device.vitalSigns.bloodOxygen,
            stressLevel
          };

          console.log('Health stat for device:', device.deviceId, healthStat);
          return healthStat;
        });

        console.log('All health stats:', stats);
        setHealthStats(stats);
      }
    } catch (error) {
      console.error('Error fetching health data for all devices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch health data when component mounts or date changes
  useEffect(() => {
    if (devices.length > 0) {
      fetchHealthDataForAllDevices(selectedDate);
    }
  }, [devices, selectedDate]);

  // Listen for date changes
  useEffect(() => {
    const handleDateChange = async (e: Event) => {
      try {
        const customEvent = e as CustomEvent;
        const selectedDateFromEvent = customEvent.detail;

        let formattedDate: string;
        if (typeof selectedDateFromEvent === 'string') {
          formattedDate = selectedDateFromEvent;
        } else if (selectedDateFromEvent instanceof Date) {
          const year = selectedDateFromEvent.getFullYear();
          const month = String(selectedDateFromEvent.getMonth() + 1).padStart(2, '0');
          const day = String(selectedDateFromEvent.getDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        } else {
          formattedDate = moment().format('YYYY-MM-DD');
        }
        
        setSelectedDate(formattedDate);
        await fetchHealthDataForAllDevices(formattedDate);
      } catch (error) {
        console.error('Error handling date change:', error);
      }
    };

    document.addEventListener('datepicker-range-end', handleDateChange);

    return () => {
      document.removeEventListener('datepicker-range-end', handleDateChange);
    };
  }, [sortedDevices]);

  // Filter devices based on search and group
  const filteredDevices = useMemo(() => {
    console.log('VitalSigns - Filtering devices:', devices.length);
    
    return sortedDevices.filter((device) => {
      // IMPORTANT: Only show devices that are bound to employees
      if (!device.idEmployee) {
        return false;
      }

      // Search filter - allow empty search to show all
      if (searchQuery.trim()) {
        const matchesSearch =
          device.employee?.Nama?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          device.employee?.NIP?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          device.idEmployee?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          device.name?.toLowerCase().includes(searchQuery.toLowerCase());

        if (!matchesSearch) return false;
      }

      // Group filter
      if (selectedGroupId && device.group_id !== selectedGroupId) {
        return false;
      }

      return true;
    });
  }, [sortedDevices, searchQuery, selectedGroupId]);

  const handleDeviceClick = (deviceId: string) => {
    setActiveDeviceId(deviceId);
    document.dispatchEvent(
      new CustomEvent('device-select', {
        detail: { deviceId },
      }),
    );
  };

  // Get health stats for a specific device
  const getDeviceHealthStats = (deviceId: string): DeviceHealthStats | null => {
    return healthStats.find(stats => stats.deviceId === deviceId) || null;
  };

  // Helper function to render health badges
  const renderHealthBadges = (deviceId: string) => {
    const stats = getDeviceHealthStats(deviceId);
    console.log('Rendering badges for device:', deviceId, 'stats:', stats, 'all stats:', healthStats.length);
    
    return (
      <div className="flex flex-wrap gap-2 mt-3">
        {/* Heart Rate Badge */}
        <div className="flex items-center gap-1 bg-red-50 text-red-700 px-3 py-1 rounded-md text-xs font-medium border border-red-200">
          <span className="text-red-500">❤️</span>
          <span>{stats?.heartRate ? `${stats.heartRate}bpm` : '00bpm'}</span>
        </div>

        {/* Blood Oxygen Badge */}
        <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-md text-xs font-medium border border-blue-200">
          <span className="text-blue-500">🫁</span>
          <span>{stats?.bloodOxygen ? `${stats.bloodOxygen}%` : '00%'}</span>
        </div>

        {/* Stress Level Badge */}
        <div className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium border ${
          stats?.stressLevel === 'High' ? 'bg-red-50 text-red-700 border-red-200' :
          stats?.stressLevel === 'Medium' ? 'bg-orange-50 text-orange-700 border-orange-200' :
          stats?.stressLevel === 'Low' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
          stats?.stressLevel === 'Relaxed' ? 'bg-green-50 text-green-700 border-green-200' :
          'bg-gray-50 text-gray-600 border-gray-200'
        }`}>
          <span className={
            stats?.stressLevel === 'High' ? 'text-red-500' :
            stats?.stressLevel === 'Medium' ? 'text-orange-500' :
            stats?.stressLevel === 'Low' ? 'text-yellow-500' :
            stats?.stressLevel === 'Relaxed' ? 'text-green-500' :
            'text-gray-500'
          }>🧠</span>
          <span>{stats?.stressLevel ? `${stats.stressLevel} Stress` : 'No Data'}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-white rounded-lg p-6 ${className}`}>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">List Employee</h3>
        <span className="text-sm text-gray-500">
          {filteredDevices.length} employee{filteredDevices.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Search and Filter Controls */}
      <div className="mb-6 space-y-3">
        {/* Search Input */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search by name, NIP, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <svg
            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Group Filter */}
        <div>
          <label htmlFor='group-filter' className='block text-sm font-medium text-gray-700 mb-2'>
            Filter Group
          </label>
          <select
            id='group-filter'
            value={selectedGroupId || ''}
            onChange={(e) => setSelectedGroupId(e.target.value || null)}
            className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          >
            <option value=''>All Groups</option>
            {groups.map(group => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Employee List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="inline-flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading health data...
            </div>
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div>No employees found matching your search.</div>
            <div className='text-xs text-gray-400 mt-2'>
              Total devices: {devices.length}, Filtered: {filteredDevices.length}
            </div>
            <div className='text-xs text-gray-400'>
              Devices with employee: {devices.filter(d => d.employee || d.idEmployee).length}
            </div>
          </div>
        ) : (
          filteredDevices.map((device) => (
            <div
              key={device.id}
              onClick={() => handleDeviceClick(device.id)}
              className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                activeDeviceId === device.id
                  ? 'border-blue-500 bg-blue-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900">
                      {device.employee?.Nama || device.name || 'Unnamed'}
                    </h4>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      device.group?.name 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {device.group?.name || 'Group not set'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 text-xs">
                    {device.employee?.Jabatan || 'Not Assigned'} - {device.employee?.NIP || device.idEmployee || 'Not Assigned'}
                  </p>
                  
                  {/* Health Badges */}
                  {renderHealthBadges(device.id)}
                </div>
                
                {activeDeviceId === device.id && (
                  <div className="ml-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
